import React, { useRef, useState, useEffect, useCallback } from 'react';
import './CameraCapture.css';
import { reportError } from '../../shared/sentry';

// Constants
const PHOTO_WIDTH = 640;
const MAX_PHOTO_PIXELS = 1280 * 1920; // keep client-side canvas reasonable
const VIDEO_WIDTH = 1280;
const VIDEO_HEIGHT = 720;
const MIME_TYPE_VIDEO = 'video/webm';
const MIME_TYPE_PHOTO = 'image/jpeg';

const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

// Matches how `object-fit: cover` crops: returns the source rect (sx/sy/sWidth/sHeight)
// that maps to a destination with `targetAspect` (dstW/dstH).
const computeCoverSourceRect = (sourceWidth, sourceHeight, targetAspect) => {
  const sourceAspect = sourceWidth / sourceHeight;

  let sx = 0;
  let sy = 0;
  let sWidth = sourceWidth;
  let sHeight = sourceHeight;

  if (sourceAspect > targetAspect) {
    // Source is wider than destination: crop horizontally.
    sWidth = sourceHeight * targetAspect;
    sx = (sourceWidth - sWidth) / 2;
  } else if (sourceAspect < targetAspect) {
    // Source is taller than destination: crop vertically.
    sHeight = sourceWidth / targetAspect;
    sy = (sourceHeight - sHeight) / 2;
  }

  return { sx, sy, sWidth, sHeight };
};

const formatElapsedSeconds = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad2 = (n) => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad2(minutes)}:${pad2(seconds)}` : `${minutes}:${pad2(seconds)}`;
};

const CameraCapture = ({ onCapture, photoOnly = false }) => {
  const videoRef = useRef(null);
  const photoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingRafIdRef = useRef(null);
  const recordingTimerRafIdRef = useRef(null);
  const recordingStartAtMsRef = useRef(null);
  const recordingLastDisplayedSecRef = useRef(-1);
  const recordingCanvasRef = useRef(null);
  const recordingCanvasStreamRef = useRef(null);
  const videoStreamWrapperRef = useRef(null);
  const photoObjectUrlRef = useRef(null);
  const videoObjectUrlRef = useRef(null);

  const [photoBlob, setPhotoBlob] = useState(null);
  const [photoObjectUrl, setPhotoObjectUrl] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoObjectUrl, setVideoObjectUrl] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordingElapsedSec, setRecordingElapsedSec] = useState(0);
  const [mode, setMode] = useState('photo');
  const [stream, setStream] = useState(null);
  const [activeView, setActiveView] = useState('loading');
  const [cameraFacingMode, setCameraFacingMode] = useState('user');
  const [frontDeviceId, setFrontDeviceId] = useState(null);
  const [backDeviceId, setBackDeviceId] = useState(null);
  const [hasEnumeratedDevices, setHasEnumeratedDevices] = useState(false);
  const [showMobileSideActions] = useState(isMobileDevice);
  const [captureBusy, setCaptureBusy] = useState(false);

  const getTargetAspectFromPreview = () => {
    const el = videoStreamWrapperRef.current;
    // Fallback: original 16:9 behavior (should only happen during initial render).
    if (!el) return 16 / 9;
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return 16 / 9;
    return rect.width / rect.height;
  };

  const computePhotoCanvasSize = (targetAspect) => {
    // Start from a stable width to keep quality predictable.
    let width = PHOTO_WIDTH;
    let height = Math.round(width / targetAspect);

    // If we’re in a very tall portrait aspect, cap pixels to keep performance ok.
    let pixels = width * height;
    if (pixels > MAX_PHOTO_PIXELS) {
      width = Math.round(Math.sqrt(MAX_PHOTO_PIXELS * targetAspect));
      height = Math.round(width / targetAspect);
    }

    return { width, height };
  };

  // Start camera stream
  const startCamera = useCallback(async () => {
    try {
      setActiveView('loading');

      const getPreferredDeviceId =
        cameraFacingMode === 'user' ? frontDeviceId : backDeviceId;

      let newStream;
      try {
        // Prefer deviceId-based switching when we have it (more reliable on iOS).
        if (getPreferredDeviceId) {
          newStream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: VIDEO_WIDTH,
              height: VIDEO_HEIGHT,
              deviceId: { exact: getPreferredDeviceId },
            },
            audio: !photoOnly,
          });
        } else {
          newStream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: VIDEO_WIDTH,
              height: VIDEO_HEIGHT,
              facingMode: { ideal: cameraFacingMode },
            },
            audio: !photoOnly,
          });
        }
      } catch (primaryErr) {
        // Fallbacks:
        // 1) facingMode without exact
        // 2) in case deviceId switching isn't supported
        newStream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: VIDEO_WIDTH,
            height: VIDEO_HEIGHT,
            facingMode: cameraFacingMode,
          },
          audio: !photoOnly,
        });
      }
      setStream(newStream);
    } catch (err) {
      reportError(err, 'CameraCapture getUserMedia');
      setStream(null);
      setActiveView('error');
    }
  }, [cameraFacingMode, frontDeviceId, backDeviceId, photoOnly]);

  const enumerateVideoDevices = useCallback(async () => {
    if (!navigator?.mediaDevices?.enumerateDevices) return;
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoInputs = devices.filter(d => d.kind === 'videoinput');

    // Labels are only available after user grants permission on most browsers.
    const front =
      videoInputs.find(d => /front|user|self/i.test(d.label)) || null;
    const back =
      videoInputs.find(d => /back|rear|environment/i.test(d.label)) || null;

    // If labels are missing entirely, relying on deviceId order may swap cameras.
    // In that case, fall back to `facingMode: { ideal: ... }` which lets the browser decide.
    if (!front && !back) {
      setFrontDeviceId(null);
      setBackDeviceId(null);
      setHasEnumeratedDevices(true);
      return;
    }

    setFrontDeviceId(front?.deviceId ?? null);
    setBackDeviceId(back?.deviceId ?? null);
    setHasEnumeratedDevices(true);
  }, []);

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [stream]);

  const stopRecordingElapsedTimer = useCallback(() => {
    if (recordingTimerRafIdRef.current) {
      cancelAnimationFrame(recordingTimerRafIdRef.current);
      recordingTimerRafIdRef.current = null;
    }
    recordingStartAtMsRef.current = null;
    recordingLastDisplayedSecRef.current = -1;
    setRecordingElapsedSec(0);
  }, []);

  useEffect(() => {
    // If the component unmounts mid-recording, stop the timer loop.
    return () => {
      stopRecordingElapsedTimer();
    };
  }, [stopRecordingElapsedTimer]);

  // Capture photo from video stream
  const takePhoto = () => {
    if (captureBusy) return;

    const targetAspect = getTargetAspectFromPreview();
    const { width, height } = computePhotoCanvasSize(targetAspect);
    const video = videoRef.current;
    const photo = photoRef.current;

    if (!video || !photo || !video.videoWidth || !video.videoHeight) return;

    photo.width = width;
    photo.height = height;
    const ctx = photo.getContext('2d');
    if (!ctx) return;
    const sourceWidth = video.videoWidth;
    const sourceHeight = video.videoHeight;
    const computedAspect = width / height;
    const { sx, sy, sWidth, sHeight } = computeCoverSourceRect(
      sourceWidth,
      sourceHeight,
      computedAspect
    );

    // Front camera previews are mirrored via CSS; mirror the bitmap we save too.
    if (cameraFacingMode === 'user') {
      ctx.save();
      ctx.translate(width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, sx, sy, sWidth, sHeight, 0, 0, width, height);
      ctx.restore();
    } else {
      ctx.drawImage(video, sx, sy, sWidth, sHeight, 0, 0, width, height);
    }

    setCaptureBusy(true);

    const handleBlob = (blob) => {
      setCaptureBusy(false);
      if (!blob) return;
      if (photoObjectUrlRef.current) {
        URL.revokeObjectURL(photoObjectUrlRef.current);
      }
      const url = URL.createObjectURL(blob);
      photoObjectUrlRef.current = url;
      setPhotoObjectUrl(url);
      setPhotoBlob(blob);
      setActiveView('photoPreview');
    };

    // `canvas.toBlob` is smoother than base64; keep a best-effort fallback for older browsers.
    if (typeof photo.toBlob === 'function') {
      try {
        photo.toBlob(handleBlob, MIME_TYPE_PHOTO, 0.92);
        return;
      } catch (e) {
        // Fall back to toDataURL below.
      }
    }

    const dataUrl = photo.toDataURL(MIME_TYPE_PHOTO);
    const arr = dataUrl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : MIME_TYPE_PHOTO;
    const bstr = atob(arr[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    handleBlob(new Blob([u8arr], { type: mime }));
  };

  // Start video recording
  const startRecording = () => {
    if (!stream) return;
    if (mediaRecorderRef.current?.state === 'recording') return;

    // Start elapsed time counter (used by the small REC pill overlay).
    stopRecordingElapsedTimer();
    recordingStartAtMsRef.current = Date.now();
    recordingLastDisplayedSecRef.current = -1;
    setRecordingElapsedSec(0);

    const tick = () => {
      const startAt = recordingStartAtMsRef.current;
      if (startAt == null) return;

      const elapsedMs = Date.now() - startAt;
      const elapsedSec = Math.floor(elapsedMs / 1000);

      if (elapsedSec !== recordingLastDisplayedSecRef.current) {
        recordingLastDisplayedSecRef.current = elapsedSec;
        setRecordingElapsedSec(elapsedSec);
      }

      recordingTimerRafIdRef.current = requestAnimationFrame(tick);
    };

    recordingTimerRafIdRef.current = requestAnimationFrame(tick);

    const wrapperEl = videoStreamWrapperRef.current;
    const rect = wrapperEl?.getBoundingClientRect?.();

    const dpr = window.devicePixelRatio || 1;
    let outW = rect?.width ? Math.round(rect.width * dpr) : VIDEO_WIDTH;
    let outH = rect?.height ? Math.round(rect.height * dpr) : VIDEO_HEIGHT;

    // Cap resolution for performance.
    const MAX_VIDEO_PIXELS = 1080 * 1920;
    const pixels = outW * outH;
    if (pixels > MAX_VIDEO_PIXELS) {
      const scale = Math.sqrt(MAX_VIDEO_PIXELS / pixels);
      outW = Math.max(1, Math.round(outW * scale));
      outH = Math.max(1, Math.round(outH * scale));
    }

    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return;

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    recordingCanvasRef.current = canvas;

    const ctx = canvas.getContext('2d', { alpha: false });
    const targetAspect = outW / outH;

    const drawFrame = () => {
      if (!videoRef.current || !recordingCanvasRef.current) return;
      const v = videoRef.current;
      if (!v.videoWidth || !v.videoHeight) return;

      const sourceWidth = v.videoWidth;
      const sourceHeight = v.videoHeight;
      const { sx, sy, sWidth, sHeight } = computeCoverSourceRect(
        sourceWidth,
        sourceHeight,
        targetAspect
      );

      if (cameraFacingMode === 'user') {
        ctx.save();
        ctx.translate(outW, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(v, sx, sy, sWidth, sHeight, 0, 0, outW, outH);
        ctx.restore();
      } else {
        ctx.drawImage(v, sx, sy, sWidth, sHeight, 0, 0, outW, outH);
      }

      recordingRafIdRef.current = requestAnimationFrame(drawFrame);
    };

    recordingRafIdRef.current = requestAnimationFrame(drawFrame);

    const canvasStream = canvas.captureStream(30);
    recordingCanvasStreamRef.current = canvasStream;

    // Best-effort audio preservation: add audio track from camera stream.
    const audioTracks = stream.getAudioTracks?.() ?? [];
    if (!photoOnly && audioTracks.length) {
      try {
        canvasStream.addTrack(audioTracks[0]);
      } catch (e) {
        // If the browser rejects it, recording will proceed without audio.
      }
    }

    let recorder;
    try {
      recorder = new MediaRecorder(canvasStream, { mimeType: MIME_TYPE_VIDEO });
    } catch (e) {
      recorder = new MediaRecorder(canvasStream);
    }

    const chunks = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size) {
        chunks.push(e.data);
      }
    };

    recorder.onstop = () => {
      // Ensure the canvas draw loop is stopped even if the recorder stops unexpectedly.
      if (recordingRafIdRef.current) {
        cancelAnimationFrame(recordingRafIdRef.current);
        recordingRafIdRef.current = null;
      }
      if (recordingCanvasStreamRef.current) {
        recordingCanvasStreamRef.current
          .getVideoTracks()
          .forEach(track => track.stop());
        recordingCanvasStreamRef.current = null;
      }
      recordingCanvasRef.current = null;

      const blob = new Blob(chunks, { type: MIME_TYPE_VIDEO });
      if (videoObjectUrlRef.current) {
        URL.revokeObjectURL(videoObjectUrlRef.current);
      }
      const url = URL.createObjectURL(blob);
      videoObjectUrlRef.current = url;
      setVideoObjectUrl(url);
      setVideoBlob(blob);
      setActiveView('videoPreview');
    };

    mediaRecorderRef.current = recorder;
    recorder.start();
    setRecording(true);
  };

  // Stop video recording
  const stopRecording = () => {
    stopRecordingElapsedTimer();

    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }

    if (recordingRafIdRef.current) {
      cancelAnimationFrame(recordingRafIdRef.current);
      recordingRafIdRef.current = null;
    }

    if (recordingCanvasStreamRef.current) {
      // Only stop the canvas video track (avoid stopping the camera’s audio track).
      recordingCanvasStreamRef.current.getVideoTracks().forEach(track => track.stop());
      recordingCanvasStreamRef.current = null;
    }

    recordingCanvasRef.current = null;
  };

  // Reset state to retake photo/video
  const retake = () => {
    if (photoObjectUrlRef.current) {
      URL.revokeObjectURL(photoObjectUrlRef.current);
      photoObjectUrlRef.current = null;
    }
    if (videoObjectUrlRef.current) {
      URL.revokeObjectURL(videoObjectUrlRef.current);
      videoObjectUrlRef.current = null;
    }

    setPhotoObjectUrl(null);
    setVideoObjectUrl(null);
    setPhotoBlob(null);
    setVideoBlob(null);
    setRecording(false);
    stopRecordingElapsedTimer();
    setActiveView('video');
  };

  // Save captured media
  const saveMedia = () => {
    if (photoBlob) {
      const filename = `photo_${Date.now()}.jpeg`;
      const file = new File([photoBlob], filename, { type: MIME_TYPE_PHOTO });
      onCapture(file);
    } else if (videoBlob) {
      const filename = `video_${Date.now()}.webm`;
      const file = new File([videoBlob], filename, { type: MIME_TYPE_VIDEO });
      onCapture(file);
    }
    stopCamera();
  };

  // Attach stream to video element
  useEffect(() => {
    if (!videoRef.current || !stream) return;

    const videoEl = videoRef.current;

    if (videoEl.srcObject !== stream) {
      videoEl.srcObject = stream;
    }

    let isCancelled = false;

    videoEl.play()
      .then(() => {
        if (!isCancelled) {
          setActiveView('video');
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          reportError(err, 'CameraCapture video play');
          setActiveView('error');
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [stream]);

  // Start camera once on mount
  useEffect(() => {
    startCamera();
  }, [startCamera]);

  // After permission, cache front/back device ids (so flip works reliably).
  useEffect(() => {
    if (!stream || hasEnumeratedDevices) return;
    enumerateVideoDevices().catch(err => {
      reportError(err, 'CameraCapture enumerateDevices');
    });
  }, [stream, hasEnumeratedDevices, enumerateVideoDevices]);

  // Stop camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Revoke any remaining preview object URLs on unmount.
  useEffect(() => {
    return () => {
      if (photoObjectUrlRef.current) {
        URL.revokeObjectURL(photoObjectUrlRef.current);
        photoObjectUrlRef.current = null;
      }
      if (videoObjectUrlRef.current) {
        URL.revokeObjectURL(videoObjectUrlRef.current);
        videoObjectUrlRef.current = null;
      }
    };
  }, []);

  const handleMainCapture = () => {
    if (mode === 'photo') {
      takePhoto();
      return;
    }

    if (mediaRecorderRef.current?.state !== 'recording') {
      startRecording();
    } else {
      stopRecording();
    }
  };

  const switchCamera = async () => {
    stopCamera();
    setActiveView('loading');
    setCameraFacingMode(prev => (prev === 'environment' ? 'user' : 'environment'));
  };

  return (
    <div className="camera-container">
      <div className="video-stream-wrapper" ref={videoStreamWrapperRef}>
        {activeView === 'loading' && (
          <div className="camera-loading-overlay">Starting Camera...</div>
        )}
        {activeView === 'error' && (
          <div className="camera-loading-overlay">Camera Error</div>
        )}

        <video
          ref={videoRef}
          className={`video-feed ${activeView !== 'video' ? 'hidden' : ''} ${cameraFacingMode === 'user' ? 'unmirror-front-camera' : ''
            }`}
          autoPlay
          muted
          playsInline
        />
        <canvas
          ref={photoRef}
          className={`photo-canvas ${activeView !== 'photoPreview' ? 'hidden' : ''}`}
        />
        {photoObjectUrl && (
          <img
            src={photoObjectUrl}
            alt="Captured"
            className={`preview-image ${activeView !== 'photoPreview' ? 'hidden' : ''}`}
          />
        )}
        {videoObjectUrl && (
          <video
            src={videoObjectUrl}
            controls
            className={`preview-video ${activeView !== 'videoPreview' ? 'hidden' : ''}`}
          />
        )}

        {activeView === 'video' && <div className="camera-grid-overlay" />}
        {mode === 'video' && activeView !== 'videoPreview' && (
          <div className="recording-indicator">
            REC <span className="recording-indicator-time">{formatElapsedSeconds(recordingElapsedSec)}</span>
          </div>
        )}
      </div>

      {(activeView === 'photoPreview' || activeView === 'videoPreview') && (
        <div className="camera-preview-bottom-sheet">
          <div className="camera-preview-action-banner" role="toolbar" aria-label="Captured media actions">
            <button type="button" onClick={retake} className="camera-banner-button camera-banner-button-cancel">
              Cancel
            </button>
            <button type="button" onClick={saveMedia} className="camera-banner-button camera-banner-button-primary">
              Add Content
            </button>
          </div>
          <div className="camera-preview-bar-spacer" aria-hidden />
        </div>
      )}

      <div
        className={`camera-controls ${activeView === 'loading' ? 'hidden' : ''} ${activeView === 'photoPreview' || activeView === 'videoPreview' ? 'hidden' : ''
          }`}
      >
        {activeView === 'video' && (
          <>
            {showMobileSideActions && (
              <button
                className="camera-side-action camera-side-action-right"
                type="button"
                onClick={switchCamera}
                disabled={recording || captureBusy}
              >
                <i className="fa fa-refresh" />
              </button>
            )}
            <button
              type="button"
              onClick={handleMainCapture}
              className={`camera-shutter ${mode === 'video' ? 'video-mode' : ''} ${recording ? 'recording' : ''}`}
              disabled={captureBusy}
            >
              <span className="camera-shutter-inner" />
            </button>
            {!photoOnly ? (
              <div className="camera-mode-switch">
                <button
                  type="button"
                  className={`camera-mode-pill ${mode === 'video' ? 'active' : ''}`}
                  disabled={recording || captureBusy}
                  onClick={() => setMode('video')}
                >
                  VIDEO
                </button>
                <button
                  type="button"
                  className={`camera-mode-pill ${mode === 'photo' ? 'active' : ''}`}
                  disabled={recording || captureBusy}
                  onClick={() => setMode('photo')}
                >
                  PHOTO
                </button>
              </div>
            ) : (
              <div className="camera-mode-switch-placeholder" />
            )}
          </>
        )}
      </div>

    </div>
  );
};

export default CameraCapture;
